// Vérification discrète du panier (« sécurité anti-oubli », 2026-10-24).
// Repère un panier où toutes les chaussettes physiques vont par deux sauf une seule chaussette à
// l'unité (cas réel : une paire Cozy Bleu + une paire Cozy Beige + 1 On Fire Rose, la On Fire Fuchsia
// avait été oubliée), et propose de vérifier, sans jamais bloquer le paiement.
// Une paire compte toujours pour 2 chaussettes physiques ; une chaussette à l'unité pour 1.
//
// Deux parties séparées :
//   1. evaluateCartPotentialMistake(cart, options) : logique pure, sans DOM, testée sous Node
//      (.claude/scripts/cart-check/evaluate.test.js).
//   2. <qn-cart-check> : encart dans le tiroir panier, puis popup au clic sur le paiement. Le Liquid (snippets/qn-cart-check.liquid)
//      fournit les lignes du panier et le complément en JSON ; le tiroir étant re-rendu par Shopify
//      à chaque modification, l'élément est recréé et réévalue le panier tout seul
//      (connectedCallback) : ni écouteur du panier, ni polling, ni appel API supplémentaire.
// Réglages et textes : Paramètres du thème > « Vérification du panier ».
(function (root) {
  'use strict';

  // ------------------------------------------------------------------
  // 1. Logique de détection (pure)
  // ------------------------------------------------------------------

  // Barème centralisé : aucune autre valeur magique dans le code.
  var DEFAULT_CONFIG = {
    threshold: 70,
    // Paliers de remise (en chaussettes à l'unité) : un total impair égal à un palier peut être voulu.
    discountTiers: [3, 6, 9],
    weights: {
      oddTotal: 30, // A : nombre total de chaussettes impair
      singleOddLine: 30, // B : une seule ligne a une quantité impaire
      restPaired: 20, // C : toutes les autres lignes vont par deux, et il y en a au moins une
      knownComplement: 30, // D : complément connu, disponible dans la même taille, absent du panier
      weakContext: -20, // une seule paire à côté de la chaussette seule (ex. 2 + 1)
      isolatedMultiple: -30, // la ligne impaire compte 3 chaussettes ou plus : choix délibéré
      oddDiscountTier: -30 // nombre de chaussettes à l'unité pile sur un palier de remise (3 ou 9)
    }
  };

  function mergeConfig(options) {
    var cfg = { threshold: DEFAULT_CONFIG.threshold, discountTiers: DEFAULT_CONFIG.discountTiers.slice(), weights: {}, suggest: true };
    Object.keys(DEFAULT_CONFIG.weights).forEach(function (k) { cfg.weights[k] = DEFAULT_CONFIG.weights[k]; });
    if (!options) return cfg;
    if (typeof options.threshold === 'number' && isFinite(options.threshold)) cfg.threshold = options.threshold;
    if (Array.isArray(options.discountTiers)) cfg.discountTiers = options.discountTiers.filter(function (n) { return n > 0; });
    if (options.weights) Object.keys(options.weights).forEach(function (k) { cfg.weights[k] = options.weights[k]; });
    cfg.suggest = options.suggest !== false;
    return cfg;
  }

  // Signature de la composition (chaussettes à l'unité et paires, variante et quantité) : sert à ne
  // pas reposer la même question tant que le panier n'a pas changé.
  function cartSignature(lines) {
    return lines
      .filter(function (l) { return (l.eligible || l.pair) && l.quantity > 0; })
      .map(function (l) { return (l.pair ? 'p' : '') + String(l.variantId) + ':' + l.quantity; })
      .sort()
      .join('|');
  }

  // cart.lines : [{ variantId, handle, title, quantity, eligible, pair, complement? }]
  //   eligible   = chaussette vendue à l'unité (tag « unite ») : seule à pouvoir être « la chaussette seule ».
  //   pair       = produit paire (2 chaussettes déjà appariées) : compte comme contexte, jamais isolé.
  //   Les autres produits (cartes cadeau, etc.) sont ignorés.
  //   complement = { handle, title, variantId, price, image, available } : chaussette associée
  //                (métachamp custom.pair_suggestion), même taille, ou absent.
  function evaluateCartPotentialMistake(cart, options) {
    var cfg = mergeConfig(options);
    var w = cfg.weights;
    var all = ((cart && cart.lines) || []).filter(function (l) { return l && l.quantity > 0; });
    var lines = all.filter(function (l) { return l.eligible; });
    var pairLines = all.filter(function (l) { return l.pair && !l.eligible; });
    var unitTotal = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
    var pairSocks = pairLines.reduce(function (s, l) { return s + l.quantity * 2; }, 0);
    var total = unitTotal + pairSocks;
    var result = {
      shouldWarn: false,
      score: 0,
      reason: 'no_unit_socks',
      signals: [],
      isolatedItem: null,
      suggestedComplement: null,
      unitQuantity: unitTotal,
      sockQuantity: total,
      signature: cartSignature(all)
    };
    if (!lines.length) return result;

    var score = 0;
    function add(name, points) { score += points; result.signals.push(name); }

    // A (les paires ajoutent toujours un nombre pair : la parité dépend des seules chaussettes à l'unité)
    if (total % 2 === 0) { result.reason = 'even_total'; return result; }
    add('odd_total', w.oddTotal);

    // B
    var oddLines = lines.filter(function (l) { return l.quantity % 2 === 1; });
    if (oddLines.length !== 1) {
      result.score = score;
      result.reason = 'several_odd_lines'; // composition volontairement variée (1 + 1 + 1, 2 + 1 + 1…)
      return result;
    }
    add('single_odd_line', w.singleOddLine);
    var isolated = oddLines[0];
    result.isolatedItem = isolated;
    if (isolated.quantity >= 3) add('isolated_multiple', w.isolatedMultiple);

    // C : sans autre ligne (1 seule chaussette, ou 3 identiques), rien ne suggère un oubli.
    // Les produits paires comptent ici comme des paires déjà complètes.
    var rest = lines.filter(function (l) { return l !== isolated; });
    if (!rest.length && !pairLines.length) {
      result.score = score;
      result.reason = 'single_line';
      return result;
    }
    add('rest_paired', w.restPaired);
    var restQuantity = rest.reduce(function (s, l) { return s + l.quantity; }, 0) + pairSocks;
    if (restQuantity < 4) add('weak_context', w.weakContext);
    // Les remises automatiques ne portent que sur les chaussettes à l'unité : palier comparé à leur nombre.
    if (cfg.discountTiers.indexOf(unitTotal) !== -1) add('odd_discount_tier', w.oddDiscountTier);

    // D : le complément doit être disponible, dans la même taille (vérifié par le Liquid), et pas déjà au panier.
    // Auto-association : si la chaussette associée configurée est la chaussette elle-même (choix de
    // l'Admin, ex. Mardi Cuicui), son complément naturel est une 2e exemplaire, s'il en reste en stock.
    var c = isolated.complement;
    if (c && c.handle && c.handle === isolated.handle) {
      if (isolated.sameAvailable) {
        add('known_complement', w.knownComplement);
        if (cfg.suggest) {
          result.suggestedComplement = {
            handle: isolated.handle,
            title: isolated.title,
            variantId: isolated.variantId,
            price: isolated.price,
            image: isolated.image,
            available: true,
            self: true
          };
        }
      }
    } else if (c && c.available && c.variantId) {
      var inCart = lines.some(function (l) {
        return String(l.variantId) === String(c.variantId) || (c.handle && l.handle === c.handle);
      });
      if (!inCart) {
        add('known_complement', w.knownComplement);
        if (cfg.suggest) result.suggestedComplement = c;
      }
    }

    result.score = Math.max(0, Math.min(100, score));
    result.shouldWarn = score >= cfg.threshold;
    result.reason = result.shouldWarn ? 'isolated_sock_among_pairs' : 'below_threshold';
    return result;
  }

  var api = {
    evaluateCartPotentialMistake: evaluateCartPotentialMistake,
    cartSignature: cartSignature,
    DEFAULT_CONFIG: DEFAULT_CONFIG
  };

  // Tests Node : on s'arrête ici, sans DOM.
  if (typeof window === 'undefined' && typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  root.QnCartCheck = api;

  // ------------------------------------------------------------------
  // 2. Affichage : encart du tiroir (niveau 1) et popup au paiement (niveau 2)
  // ------------------------------------------------------------------

  // État central, partagé par l'encart et la popup, en mémoire de session et lié à la signature
  // du panier : toute modification réelle (ajout, retrait, quantité, variante, paire) donne une
  // autre signature, donc une nouvelle évaluation.
  //   ack     : composition validée par le client (« C'est voulu » ou « parfaite comme ça »).
  //   popup   : composition qui a déjà eu sa popup (une seule interception du paiement).
  //   shown   : composition dont l'encart a déjà été compté comme vu.
  //   pending : ajout lancé depuis l'encart ou la popup, pour le mesurer après le re-rendu.
  var STORE_KEYS = {
    ack: 'qn_cart_safety_ack',
    popup: 'qn_cart_safety_popup',
    shown: 'qn_cart_safety_shown',
    pending: 'qn_cart_safety_pending'
  };

  // Copie en mémoire : si sessionStorage est indisponible (certaines navigations privées), l'état
  // tient au moins jusqu'au rechargement, et la popup ne revient jamais à chaque clic.
  var memory = {};
  var sessionOk = (function () {
    try {
      root.sessionStorage.setItem('qn_cart_safety_test', '1');
      root.sessionStorage.removeItem('qn_cart_safety_test');
      return true;
    } catch (e) { return false; }
  })();

  function store(key, value) {
    if (sessionOk) {
      try {
        if (value === undefined) return root.sessionStorage.getItem(key);
        if (value === null) root.sessionStorage.removeItem(key);
        else root.sessionStorage.setItem(key, value);
        return null;
      } catch (e) { sessionOk = false; /* devenu indisponible : copie en mémoire */ }
    }
    if (value === undefined) return Object.prototype.hasOwnProperty.call(memory, key) ? memory[key] : null;
    if (value === null) delete memory[key];
    else memory[key] = value;
    return null;
  }

  var cartSafety = {
    isAcknowledged: function (sig) { return store(STORE_KEYS.ack) === sig; },
    acknowledge: function (sig) { store(STORE_KEYS.ack, sig); },
    popupDone: function (sig) { return store(STORE_KEYS.popup) === sig; },
    markPopup: function (sig) { store(STORE_KEYS.popup, sig); }
  };

  // Événements dataLayer à plat, comme pack_* et machine_* ; aucune donnée personnelle.
  // N'atteignent GA4 que via une balise GTM relais.
  function track(eventName, result, extra) {
    root.dataLayer = root.dataLayer || [];
    var payload = { event: eventName };
    if (result) {
      payload.cart_quantity = result.sockQuantity; // chaussettes physiques (une paire = 2)
      payload.unit_quantity = result.unitQuantity;
      payload.suspicion_score = result.score;
      payload.rule = result.signals.join('+');
      payload.isolated_product = result.isolatedItem ? result.isolatedItem.handle : '';
      payload.isolated_type = 'unit'; // seule une chaussette à l'unité peut être isolée
      payload.has_complement = !!result.suggestedComplement;
    }
    Object.keys(extra || {}).forEach(function (k) { payload[k] = extra[k]; });
    root.dataLayer.push(payload);
  }

  // Dans le tiroir, l'encart est calculé dès le chargement, tiroir fermé : on ne le compte comme vu
  // (et on ne l'annonce) qu'à l'ouverture réelle du tiroir. Un seul observateur, sur la classe du
  // tiroir. Sur la page panier, l'encart est visible tout de suite.
  var pendingReveal = null;
  var drawerObserver = null;

  function whenVisible(host, callback) {
    var drawer = host.closest('cart-drawer');
    if (!drawer || drawer.classList.contains('active')) {
      callback();
      return;
    }
    pendingReveal = callback;
    if (drawerObserver) return;
    drawerObserver = new MutationObserver(function () {
      if (!pendingReveal || !drawer.classList.contains('active')) return;
      var reveal = pendingReveal;
      pendingReveal = null;
      reveal();
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  // Nom court d'une chaussette pour un bouton : sans les mots qu'elle partage avec la chaussette
  // seule (« On Fire Fuchsia » à côté de « On Fire Rose » donne « Fuchsia »). Affichage seulement.
  function shortName(name, ref) {
    var a = String(name || '').split(' ');
    var b = String(ref || '').split(' ');
    var i = 0;
    while (i < a.length - 1 && i < b.length && a[i].toLowerCase() === b[i].toLowerCase()) i++;
    var s = a.slice(i).join(' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function thumb(src, size, className) {
    var img = el('img', className);
    img.src = src;
    img.alt = '';
    img.width = size;
    img.height = size;
    img.loading = 'lazy';
    return img;
  }

  // Bouton d'ajout. Dans le tiroir : même attribut que la suggestion B3, donc ajout Ajax et re-rendu
  // du tiroir et du compteur par quanailles-cro.js. Sur la page panier : addOnCartPage, qui
  // redessine la page comme un changement de quantité Dawn. Aucun rechargement dans les deux cas.
  function addButton(product, text, ariaLabel, kind, source, host) {
    var btn = el('button', 'qn-cart-check__add', text);
    btn.type = 'button';
    btn.setAttribute('aria-label', ariaLabel);
    btn.addEventListener('click', function () { store(STORE_KEYS.pending, [kind, product.variantId, source].join(':')); });
    if (host.inDrawer) {
      btn.setAttribute('data-qn-pair-add', '');
      btn.setAttribute('data-variant-id', product.variantId);
    } else {
      btn.addEventListener('click', function () { host.addOnCartPage(product.variantId, btn); });
    }
    return btn;
  }

  // Encart qui correspond à un bouton de paiement : celui du tiroir, ou celui de la page panier.
  function hostFor(checkout) {
    var scope = checkout.closest('cart-drawer') || document.getElementById('main-cart-footer');
    return scope ? scope.querySelector('qn-cart-check') : null;
  }

  class QnCartCheckElement extends HTMLElement {
    connectedCallback() {
      if (this.data) return; // déjà évalué (élément déplacé, pas re-rendu)
      // Deux emplacements : tiroir (sur toutes les pages) et page panier ; chacun porte son état
      // d'affichage, la validation du client reste commune (cartSafety).
      this.inDrawer = !!this.closest('cart-drawer');
      this.surface = this.inDrawer ? 'drawer' : 'cart_page';
      this.state = null;
      var dataEl = this.querySelector('script[type="application/json"]');
      if (!dataEl) return;
      var data;
      try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!data || !data.enabled) return;
      this.data = data;
      var result = evaluateCartPotentialMistake({ lines: data.lines }, {
        threshold: data.threshold,
        discountTiers: data.tiers,
        suggest: data.suggest
      });
      this.result = result;
      this.reportPendingAdd(result);
      if (!result.shouldWarn) return;
      if (cartSafety.isAcknowledged(result.signature)) {
        this.state = 'acknowledged';
        return;
      }
      this.render(result);
    }

    // Ajout lancé depuis l'encart ou la popup : mesuré quand le tiroir revient re-rendu avec la chaussette.
    reportPendingAdd(result) {
      var pending = store(STORE_KEYS.pending);
      if (!pending) return;
      store(STORE_KEYS.pending, null);
      var parts = pending.split(':');
      var added = this.data.lines.some(function (l) { return String(l.variantId) === parts[1]; });
      if (!added) return;
      track(parts[0] === 'same' ? 'cart_safety_same_product_added' : 'cart_safety_complement_added', result, {
        added_product: parts[1],
        source: parts[2]
      });
    }

    // Niveau 1 : encart discret au-dessus de « Procéder au paiement ». Tiroir : rendu compact
    // (2026-10-24) ; page panier : rendu d'origine. Mêmes données, mêmes actions, même état.
    render(result) {
      var s = this.data.strings;
      var isNew = store(STORE_KEYS.shown) !== result.signature;
      var box = el('div', 'qn-cart-check' + (this.inDrawer ? ' qn-cart-check--compact' : ''));
      box.setAttribute('role', 'group');
      box.setAttribute('aria-labelledby', 'QnCartCheck-Text-' + this.surface);

      var text = this.inDrawer ? this.buildCompact(result, box) : this.buildFull(result, box);

      // Annonce polie : la zone existe avant que son texte change, sinon les lecteurs d'écran l'ignorent.
      var live = el('span', 'visually-hidden');
      live.setAttribute('role', 'status');
      box.appendChild(live);

      this.appendChild(box);
      this.box = box;
      this.state = 'visible';

      var surface = this.surface;
      if (isNew) {
        whenVisible(this, function () {
          if (!box.isConnected) return; // encart re-rendu entre-temps : le nouveau prend le relais
          store(STORE_KEYS.shown, result.signature);
          track('cart_safety_shown', result, { surface: surface });
          box.classList.add('qn-cart-check--enter');
          setTimeout(function () { live.textContent = text.textContent; }, 400);
        });
      }
    }

    // Encart compact du tiroir : vignette, « Mardi Cuicui est seule », une question, puis les actions
    // disponibles : « Ajouter la même » (si stock), « Ajouter son complément » (si configuré et
    // disponible, jamais inventé), « C'est voulu ». Mêmes boutons et mêmes mesures que l'encart complet.
    buildCompact(result, box) {
      var s = this.data.strings;
      var k = s.compact;
      var isolated = result.isolatedItem;
      var c = result.suggestedComplement;
      var canSame = !!isolated.sameAvailable;
      var other = c && !c.self ? c : null; // auto-association : la même chaussette suffit

      if (isolated.image) box.appendChild(thumb(isolated.image, 36, 'qn-cart-check__thumb'));

      var text = el('div', 'qn-cart-check__intro');
      text.id = 'QnCartCheck-Text-' + this.surface;
      var title = el('p', 'qn-cart-check__title');
      var sock = el('span', 'qn-cart-check__emoji', '🧦');
      sock.setAttribute('aria-hidden', 'true');
      title.appendChild(sock);
      title.appendChild(document.createTextNode(' ' + k.title.replace('[product]', isolated.title)));
      text.appendChild(title);
      var sub = canSame && other ? k.subBoth : canSame ? k.subSame : other ? k.subComplement : k.subNone;
      text.appendChild(el('p', 'qn-cart-check__sub', sub));
      box.appendChild(text);

      var ok = el('button', 'qn-cart-check__ok', s.confirm);
      ok.type = 'button';
      ok.addEventListener('click', this.acknowledge.bind(this, this.surface));

      // « C'est voulu » rejoint la ligne d'actions quand il n'y a qu'un ajout possible ; avec deux
      // ajouts, il passe dessous, centré, en troisième option plus discrète que les boutons.
      var actions = el('div', 'qn-cart-check__actions');
      box.appendChild(actions);
      if (canSame && other) {
        ok.classList.add('qn-cart-check__ok--below');
        box.appendChild(ok);
      }
      if (canSame) {
        actions.appendChild(addButton(isolated, '+ ' + k.addSame, s.popup.sameLabel.replace('[product]', isolated.title), 'same', this.surface, this));
      }
      if (other) {
        // Bouton court (« + Ajouter Fuchsia »), libellé accessible complet
        actions.appendChild(addButton(other, '+ ' + k.addComplement.replace('[complement]', shortName(other.title, isolated.title)), s.addLabel.replace('[product]', other.title), 'complement', this.surface, this));
        this.toggleB3Row(other.variantId, true);
      }
      if (!(canSame && other)) actions.appendChild(ok);
      return text;
    }

    // Encart complet (page panier) : rendu d'origine, inchangé.
    buildFull(result, box) {
      var s = this.data.strings;
      var text = el('p', 'qn-cart-check__text');
      text.id = 'QnCartCheck-Text-' + this.surface;
      text.appendChild(el('strong', 'qn-cart-check__title', s.title));
      text.appendChild(document.createTextNode(' ' + s.body.replace('[product]', result.isolatedItem.title)));
      box.appendChild(text);

      var ok = el('button', 'qn-cart-check__ok', s.confirm);
      ok.type = 'button';
      ok.addEventListener('click', this.acknowledge.bind(this, this.surface));
      box.appendChild(ok);

      var c = result.suggestedComplement;
      if (c) {
        var row = el('div', 'qn-cart-check__suggest');
        if (c.image) row.appendChild(thumb(c.image, 40, 'qn-cart-check__img'));
        var info = el('p', 'qn-cart-check__product');
        // Auto-association : « Pour faire la paire » plutôt que « À associer avec »
        info.appendChild(el('span', 'qn-cart-check__label', c.self ? s.makePair : s.pairWith));
        info.appendChild(el('strong', 'qn-cart-check__name', c.title));
        info.appendChild(el('span', 'qn-cart-check__price', c.price));
        row.appendChild(info);
        row.appendChild(addButton(c, s.add, c.self ? s.popup.sameLabel.replace('[product]', c.title) : s.addLabel.replace('[product]', c.title), c.self ? 'same' : 'complement', this.surface, this));
        box.appendChild(row);
        this.toggleB3Row(c.variantId, true);
      }
      return text;
    }

    // Petite carte de la popup : « Même modèle » ou « Duo dépareillé ».
    card(kicker, product, ariaLabel, kind) {
      var card = el('div', 'qn-cart-check__card');
      card.appendChild(el('p', 'qn-cart-check__kicker', kicker));
      if (product.image) card.appendChild(thumb(product.image, 64, 'qn-cart-check__card-img'));
      var info = el('p', 'qn-cart-check__card-info');
      info.appendChild(el('strong', 'qn-cart-check__name', product.title));
      info.appendChild(el('span', 'qn-cart-check__price', product.price));
      card.appendChild(info);
      card.appendChild(addButton(product, this.data.strings.add, ariaLabel, kind, 'popup', this));
      return card;
    }

    // Niveau 2 : popup au clic sur « Procéder au paiement », une seule fois par composition, si
    // l'encart est resté sans réponse. Choix : la même chaussette, la chaussette associée,
    // « parfaite comme ça » (paiement immédiat). La croix, Échap ou le fond ramènent au panier
    // sans rien valider.
    openDialog(checkoutButton) {
      var self = this;
      var result = this.result;
      var p = this.data.strings.popup;
      var isolated = result.isolatedItem;
      var continued = false;

      var dialog = el('dialog', 'qn-cart-check-dialog');
      // Toutes les fermetures passent ici, directement : l'événement close du <dialog> peut être
      // retardé (onglet en arrière-plan) ou devancé par la navigation vers le paiement.
      var closed = false;
      function closeDialog() {
        if (closed) return;
        closed = true;
        if (dialog.open && typeof dialog.close === 'function') dialog.close();
        dialog.remove();
        if (continued) return;
        track('cart_safety_popup_closed', result);
        if (checkoutButton.isConnected) checkoutButton.focus();
      }
      dialog.setAttribute('aria-labelledby', 'QnCartCheck-DialogTitle-' + this.surface);
      dialog.setAttribute('aria-describedby', 'QnCartCheck-DialogText-' + this.surface);
      var box = el('div', 'qn-cart-check-dialog__box');

      var close = el('button', 'qn-cart-check__close');
      close.type = 'button';
      close.setAttribute('aria-label', p.close);
      close.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
      close.addEventListener('click', closeDialog);
      box.appendChild(close);

      var head = el('div', 'qn-cart-check-dialog__head');
      if (isolated.image) head.appendChild(thumb(isolated.image, 56, 'qn-cart-check-dialog__img'));
      var headText = el('div', 'qn-cart-check-dialog__intro');
      var title = el('h2', 'qn-cart-check-dialog__title', p.title);
      title.id = 'QnCartCheck-DialogTitle-' + this.surface;
      headText.appendChild(title);
      var text = el('p', 'qn-cart-check-dialog__text', p.body.replace('[product]', isolated.title));
      text.id = 'QnCartCheck-DialogText-' + this.surface;
      headText.appendChild(text);
      head.appendChild(headText);
      box.appendChild(head);

      // Cartes : la même chaussette si elle peut encore être ajoutée, la chaussette associée si
      // elle est disponible dans la même taille. Aucune carte n'est affichée pour un ajout impossible.
      var cards = el('div', 'qn-cart-check__cards');
      if (isolated.sameAvailable) {
        cards.appendChild(this.card(p.sameKicker, isolated, p.sameLabel.replace('[product]', isolated.title), 'same'));
      }
      // Auto-association : la carte « Même modèle » suffit, pas de seconde carte identique.
      if (result.suggestedComplement && !result.suggestedComplement.self) {
        var c = result.suggestedComplement;
        cards.appendChild(this.card(p.complementKicker, c, p.complementLabel.replace('[product]', c.title), 'complement'));
      }
      if (cards.childNodes.length) box.appendChild(cards);

      var go = el('button', 'qn-cart-check-dialog__go');
      go.type = 'button';
      go.appendChild(el('span', 'qn-cart-check-dialog__go-main', p.confirm));
      go.appendChild(el('span', 'qn-cart-check-dialog__go-sub', p.confirmSub));
      go.addEventListener('click', function () {
        continued = true;
        self.acknowledge('popup');
        closeDialog();
        // L'état est maintenant « validé » : ce clic n'est plus retenu, le paiement part tout de suite.
        checkoutButton.click();
      });
      box.appendChild(go);
      dialog.appendChild(box);

      // Échap ferme la popup sans fermer le tiroir : la popup se ferme à l'appui (cancel), Dawn ferme le
      // tiroir au relâchement (keyup) ; on intercepte ce seul relâchement, en capture (sans effet
      // sur la page panier, où rien n'écoute Échap).
      dialog.addEventListener('cancel', function (e) {
        e.preventDefault();
        document.addEventListener('keyup', function stopEscape(ev) {
          document.removeEventListener('keyup', stopEscape, true);
          if (ev.code === 'Escape') ev.stopPropagation();
        }, true);
        closeDialog();
      });
      dialog.addEventListener('close', closeDialog); // filet de sécurité (fermeture par le navigateur)
      // Clic sur le fond : ferme la popup
      dialog.addEventListener('click', function (e) { if (e.target === dialog) closeDialog(); });

      this.appendChild(dialog);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      go.focus();
      track('cart_safety_shown', result, { surface: 'popup' });
    }

    // La suggestion B3 de la même chaussette (« Associe-la avec… ») ferait doublon avec l'encart.
    toggleB3Row(variantId, hide) {
      if (!this.inDrawer) return; // la suggestion B3 n'existe que dans le tiroir
      var drawer = this.closest('cart-drawer');
      var btn = drawer.querySelector('.qn-pair-row [data-qn-pair-add][data-variant-id="' + variantId + '"]');
      var row = btn && btn.closest('tr');
      if (row) row.hidden = hide;
    }

    // Retire l'encart affiché (validation faite ici ou dans l'autre emplacement).
    clearBox() {
      if (this.result && this.result.suggestedComplement) this.toggleB3Row(this.result.suggestedComplement.variantId, false);
      if (this.box) this.box.remove();
      this.box = null;
      if (this.state) this.state = 'acknowledged';
    }

    // Validation du client, depuis l'encart (tiroir ou page) ou la popup : un seul état pour tous.
    acknowledge(source) {
      var result = this.result;
      cartSafety.acknowledge(result.signature);
      track('cart_safety_acknowledged', result, { source: source });
      document.querySelectorAll('qn-cart-check').forEach(function (host) {
        if (host.result && host.result.signature === result.signature) host.clearBox();
      });
      this.state = 'acknowledged';
      if (source !== 'popup') {
        var checkout = this.inDrawer ? document.getElementById('CartDrawer-Checkout') : document.getElementById('checkout');
        if (checkout) checkout.focus();
      }
    }

    // Page panier : ajout puis re-rendu des sections de la page comme un changement de quantité Dawn
    // (CartItems.getSectionsToRender), sans ouvrir le tiroir ni recharger la page.
    addOnCartPage(variantId, btn) {
      var items = document.querySelector('cart-items');
      if (!items || typeof items.getSectionsToRender !== 'function') return;
      var sections = items.getSectionsToRender();
      btn.disabled = true;
      if (root.QnAddError) root.QnAddError.clear(btn);
      fetch(root.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.map(function (s) { return s.section; }),
          sections_url: root.location.pathname
        })
      }).then(function (r) { return r.json(); }).then(function (response) {
        // Harmonisation du 2026-10-25 (lot A) : un échec est dit sous le bouton (QnAddError, quanailles-cro.js)
        if (response.status || !response.sections) { btn.disabled = false; if (root.QnAddError) root.QnAddError(btn, response); return; }
        if (root.QnGA4) root.QnGA4.push('add_to_cart', root.QnGA4.itemsFromCartResponse(response), 'EUR');
        sections.forEach(function (s) {
          var target = document.getElementById(s.id);
          var html = response.sections[s.section];
          if (!target || !html) return;
          var el = target.querySelector(s.selector) || target;
          el.innerHTML = items.getSectionInnerHTML(html, s.selector);
        });
        // Le tiroir (présent sur la page) se met à jour par l'abonnement Dawn au panier.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'qn-cart-check', cartData: response });
        }
      }).catch(function (err) { btn.disabled = false; if (root.QnAddError) root.QnAddError(btn, err); });
    }
  }

  if (!customElements.get('qn-cart-check')) customElements.define('qn-cart-check', QnCartCheckElement);

  // Clic sur « Procéder au paiement » (tiroir ou page panier) : popup une seule fois si l'encart est
  // resté sans réponse ; sinon le paiement part sans délai (simple mesure). Les paiements accélérés
  // (Shop Pay, PayPal…, page panier seulement) sont des iframes Shopify : non interceptés.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var checkout = e.target.closest('#CartDrawer-Checkout, #checkout');
    if (!checkout) return;
    var host = hostFor(checkout);
    if (!host || !host.state || !host.result) return;
    var sig = host.result.signature;
    if (host.state === 'visible' && host.data.popup && !cartSafety.popupDone(sig) && !cartSafety.isAcknowledged(sig)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      cartSafety.markPopup(sig);
      host.openDialog(checkout);
      return;
    }
    track('cart_safety_checkout_continued', host.result, { warning_state: host.state, surface: host.surface });
  }, { capture: true });
})(typeof window !== 'undefined' ? window : this);
